import { i18n } from "../../translate/i18n";

import React, { useState, useEffect, useContext } from "react";
import { Link as RouterLink } from "react-router-dom";

import { Typography, Popover } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Mail, Lock, Eye, EyeOff, LogIn } from "lucide-react";

import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import ThemeToggle from "../../components/ThemeToggle";
import { BACKEND_URL } from "../../config/env";

const SIGNUP_LANGUAGE_PATHS = {
  pt: "/pt/registre-se",
  en: "/en/sign-up",
  es: "/es/registrarse",
  tr: "/tr/kayit-ol",
  ar: "/ar/register",
};

// Check icon for selected language
const CheckIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M5 12L10 17L20 7" />
  </svg>
);

const useStyles = makeStyles((theme) => ({
  "@keyframes loginSpin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  "@keyframes loginFadeIn": {
    from: { opacity: 0, transform: "translateY(16px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  root: {
    display: "flex",
    width: "100vw",
    minHeight: "100vh",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    padding: "20px",
    backgroundColor: theme.mode === "light" ? "#f5f5f5" : "#0a0a0a",
    transition: "background-color 0.3s ease",
  },

  // Controles no topo direito (igual ao header do sistema)
  topRightControls: {
    position: "absolute",
    top: 16,
    right: 20,
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  // Trigger da bandeira (mesmo estilo de UserLanguageSelector)
  langTriggerButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 8,
    borderRadius: 6,
    transition: "all 0.15s ease",
    "&:hover": {
      background: theme.mode === "dark"
        ? "rgba(255,255,255,0.15)"
        : "rgba(0,0,0,0.06)",
    },
  },
  langTriggerFlag: {
    width: 28,
    height: 20,
    objectFit: "cover",
    borderRadius: 3,
    border: theme.mode === "dark"
      ? "1.5px solid rgba(255,255,255,0.3)"
      : "1.5px solid rgba(0,0,0,0.15)",
    display: "block",
  },

  // Popover de idiomas (estilo do sistema)
  langPopoverPaper: {
    borderRadius: 14,
    minWidth: 230,
    overflow: "hidden",
    boxShadow: "0 10px 36px rgba(0,0,0,0.18)",
    border: "none",
    background: theme.mode === "dark" ? "#121212" : "#ffffff",
  },
  langHeader: {
    display: "flex",
    alignItems: "center",
    padding: "10px 14px",
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText || "#ffffff",
  },
  langHeaderTitle: {
    fontWeight: 600,
    fontSize: 13,
    color: "inherit",
  },
  langOptionsList: {
    padding: 6,
  },
  langOptionItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "9px 12px",
    borderRadius: 8,
    cursor: "pointer",
    transition: "all 0.15s ease",
    "&:hover": {
      background: theme.mode === "dark"
        ? "rgba(255,255,255,0.06)"
        : "rgba(0,0,0,0.04)",
    },
  },
  langOptionItemSelected: {
    background: "rgba(6, 207, 156, 0.10)",
    "&:hover": { background: "rgba(6, 207, 156, 0.15)" },
  },
  langFlagImg: {
    width: 30,
    height: 20,
    objectFit: "cover",
    borderRadius: 3,
    flexShrink: 0,
    border: theme.mode === "dark"
      ? "1px solid rgba(255,255,255,0.12)"
      : "1px solid rgba(0,0,0,0.1)",
  },
  langOptionName: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.mode === "dark" ? "#ffffff" : "#1a1a1a",
    flex: 1,
  },
  langCheckWrapper: {
    width: 18,
    height: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.primary.main,
    flexShrink: 0,
  },

  // ===== Card =====
  cardWrap: {
    position: "relative",
    width: "100%",
    maxWidth: 460,
    animation: "$loginFadeIn 0.6s ease-out both",
  },
  blurLayer: {
    position: "absolute",
    inset: 0,
    borderRadius: 24,
    overflow: "hidden",
    pointerEvents: "none",
  },
  blurOrb1: {
    position: "absolute",
    top: -40, left: -40, right: -40, bottom: -40,
    borderRadius: "9999px",
    background: theme.mode === "light"
      ? "linear-gradient(to right, transparent, rgba(107,114,128,0.18), transparent)"
      : "linear-gradient(to right, transparent, rgba(156,163,175,0.20), transparent)",
    filter: "blur(20px)",
    opacity: theme.mode === "light" ? 0.4 : 0.5,
    animation: "$loginSpin 10s linear infinite",
  },
  blurOrb2: {
    position: "absolute",
    top: -80, left: -80, right: -80, bottom: -80,
    borderRadius: "9999px",
    background: theme.mode === "light"
      ? "linear-gradient(to right, transparent, rgba(75,85,99,0.12), transparent)"
      : "linear-gradient(to right, transparent, rgba(107,114,128,0.15), transparent)",
    filter: "blur(40px)",
    opacity: theme.mode === "light" ? 0.25 : 0.3,
    animation: "$loginSpin 18s linear infinite reverse",
  },
  borderGradient: {
    position: "absolute",
    inset: 0,
    borderRadius: 24,
    padding: 1,
    background: theme.mode === "light"
      ? "linear-gradient(to bottom, rgba(209,213,219,0.9), rgba(156,163,175,0.7), rgba(107,114,128,0.5))"
      : "linear-gradient(to bottom, rgba(209,213,219,0.4), rgba(75,85,99,0.6), rgba(31,41,55,0.5))",
    pointerEvents: "none",
    WebkitMask:
      "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude",
  },
  card: {
    position: "relative",
    overflow: "hidden",
    borderRadius: 24,
    boxShadow: theme.mode === "light"
      ? "inset 0 0 0 1px rgba(0,0,0,0.05)"
      : "inset 0 0 0 1px rgba(255,255,255,0.15)",
    backgroundColor: theme.mode === "light"
      ? "hsl(220, 13%, 95%)"
      : "#11141b",
    backgroundImage: theme.mode === "light"
      ? `
        radial-gradient(at 88% 40%, hsl(220, 13%, 95%) 0px, transparent 85%),
        radial-gradient(at 49% 30%, hsl(220, 13%, 95%) 0px, transparent 85%),
        radial-gradient(at 14% 26%, hsl(220, 13%, 95%) 0px, transparent 85%),
        radial-gradient(at 0% 64%, hsl(220, 13%, 82%) 0px, transparent 85%),
        radial-gradient(at 41% 94%, hsl(215, 14%, 86%) 0px, transparent 85%),
        radial-gradient(at 100% 99%, hsl(217, 19%, 88%) 0px, transparent 85%)
      `
      : `linear-gradient(135deg, #1f2940 0%, #161a22 45%, #11141b 100%)`,
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.mode === "light"
        ? "inset 0 0 0 1px rgba(107,114,128,0.25), 0 10px 40px -10px rgba(107,114,128,0.25)"
        : "inset 0 0 0 1px rgba(156,163,175,0.3), 0 10px 40px -10px rgba(107,114,128,0.4)",
    },
  },
  cardInner: {
    position: "relative",
    padding: 24,
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.up("sm")]: { padding: 32 },
    [theme.breakpoints.up("md")]: { padding: 40 },
  },

  logoImg: {
    display: "block",
    margin: "0 auto 16px",
    maxWidth: 150,
    height: "auto",
  },

  header: {
    marginBottom: 24,
  },
  secureRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    color: theme.mode === "light" ? "#4b5563" : "#9ca3af",
    marginBottom: 10,
  },
  secureDot: {
    height: 6,
    width: 6,
    borderRadius: "50%",
    background: theme.mode === "light"
      ? "rgba(16, 185, 129, 0.9)"
      : "rgba(52, 211, 153, 0.85)",
  },
  title: {
    fontSize: 26,
    fontWeight: 600,
    letterSpacing: "-0.01em",
    color: theme.mode === "light" ? "#111827" : "#ffffff",
    margin: 0,
    [theme.breakpoints.up("sm")]: { fontSize: 30 },
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 18,
  },

  fieldLabel: {
    display: "block",
    fontSize: 11,
    fontWeight: 500,
    color: theme.mode === "light" ? "#374151" : "#d1d5db",
    marginBottom: 6,
  },
  fieldHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  forgotLinkInline: {
    fontSize: 11,
    color: theme.mode === "light" ? "#4b5563" : "#9ca3af",
    textDecoration: "none",
    transition: "color 0.15s ease",
    "&:hover": {
      color: theme.mode === "light" ? "#111827" : "#e5e7eb",
      textDecoration: "underline",
      textUnderlineOffset: 4,
    },
  },

  inputWrap: {
    position: "relative",
  },
  inputIconLeft: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 12,
    display: "flex",
    alignItems: "center",
    color: theme.mode === "light" ? "#6b7280" : "#9ca3af",
    pointerEvents: "none",
  },
  inputIconRight: {
    position: "absolute",
    top: 0,
    bottom: 0,
    right: 12,
    display: "flex",
    alignItems: "center",
    color: theme.mode === "light" ? "#6b7280" : "#9ca3af",
    cursor: "pointer",
    background: "transparent",
    border: "none",
    padding: 0,
    "&:hover": {
      color: theme.mode === "light" ? "#111827" : "#e5e7eb",
    },
  },
  input: {
    width: "100%",
    borderRadius: 10,
    background: theme.mode === "light"
      ? "rgba(255, 255, 255, 0.85)"
      : "rgba(17, 24, 39, 0.5)",
    color: theme.mode === "light" ? "#111827" : "#f3f4f6",
    boxShadow: theme.mode === "light"
      ? "inset 0 0 0 1px rgba(156, 163, 175, 0.5)"
      : "inset 0 0 0 1px rgba(75, 85, 99, 0.3)",
    border: "none",
    outline: "none",
    padding: "10px 40px",
    fontSize: 13,
    fontFamily: "inherit",
    transition: "box-shadow 0.15s ease",
    "&::placeholder": {
      color: theme.mode === "light" ? "#9ca3af" : "#6b7280",
    },
    "&:focus": {
      boxShadow: theme.mode === "light"
        ? "inset 0 0 0 2px rgba(75, 85, 99, 0.55)"
        : "inset 0 0 0 2px rgba(156, 163, 175, 0.5)",
    },
  },
  inputWithRight: {
    paddingRight: 44,
  },

  optionsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  rememberLabel: {
    display: "inline-flex",
    alignItems: "center",
    cursor: "pointer",
    userSelect: "none",
  },
  cyberpunkCheckbox: {
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    width: 16,
    height: 16,
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: 5,
    backgroundColor: "transparent",
    display: "inline-block",
    position: "relative",
    margin: 0,
    marginRight: 8,
    padding: 0,
    cursor: "pointer",
    flexShrink: 0,
    "&::before": {
      content: '""',
      backgroundColor: theme.palette.primary.main,
      display: "block",
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%) scale(0)",
      width: 8,
      height: 8,
      borderRadius: 3,
      transition: "all 0.3s ease-in-out",
    },
    "&:checked::before": {
      transform: "translate(-50%, -50%) scale(1)",
    },
  },
  rememberText: {
    fontSize: 12,
    color: theme.mode === "light" ? "#374151" : "#d1d5db",
  },

  submitBtn: {
    width: "100%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 500,
    color: "#ffffff",
    background: "linear-gradient(to right, #374151, #4b5563)",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    boxShadow:
      "inset 0 -2px 25px -4px rgba(255,255,255,0.2), inset 0 0 0 1px rgba(255,255,255,0.10)",
    transition: "all 0.3s ease",
    fontFamily: "inherit",
    "&:hover": {
      background: "linear-gradient(to right, #4b5563, #6b7280)",
      boxShadow:
        "inset 0 -2px 25px -4px rgba(255,255,255,0.2), inset 0 0 0 1px rgba(209,213,219,0.4), 0 8px 24px -8px rgba(0,0,0,0.5)",
    },
    "&:focus": { outline: "none" },
    "&:disabled": {
      cursor: "not-allowed",
      opacity: 0.65,
    },
  },

  divider: {
    position: "relative",
    paddingTop: 4,
    paddingBottom: 4,
  },
  dividerLine: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
  },
  dividerLineInner: {
    width: "100%",
    borderTop: theme.mode === "light"
      ? "1px solid rgba(0,0,0,0.10)"
      : "1px solid rgba(255,255,255,0.10)",
  },
  dividerLabelWrap: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
  },
  dividerLabel: {
    background: "transparent",
    padding: "0 8px",
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: theme.mode === "light" ? "#6b7280" : "#6b7280",
  },

  signupRow: {
    marginTop: 24,
    fontSize: 12,
    color: theme.mode === "light" ? "#4b5563" : "#6b7280",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
  },
  signupLink: {
    color: theme.mode === "light" ? "#1f2937" : "#d1d5db",
    textDecoration: "none",
    fontWeight: 500,
    "&:hover": {
      color: theme.mode === "light" ? "#111827" : "#ffffff",
      textDecoration: "underline",
      textUnderlineOffset: 4,
    },
  },
  footerLinks: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  footerLink: {
    color: "inherit",
    textDecoration: "none",
    transition: "color 0.15s ease",
    "&:hover": { color: "#ffffff" },
  },
  footerSep: { color: "#4b5563" },
}));

const Login = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { handleLogin, loading } = useContext(AuthContext);
  const { colorMode } = useContext(ColorModeContext);

  const [user, setUser] = useState({ email: "", password: "", remember: false });
  const [showPassword, setShowPassword] = useState(false);
  const [userCreationEnabled, setUserCreationEnabled] = useState(true);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);

  const languageOptions = [
    { code: "es", shortCode: "es", flag: "/flags/es.png", name: "Español" },
    { code: "pt-BR", shortCode: "pt", flag: "/flags/br.png", name: "Português" },
    { code: "en", shortCode: "en", flag: "/flags/us.png", name: "English" },
    { code: "tr", shortCode: "tr", flag: "/flags/tr.png", name: "Türkçe" },
    { code: "ar", shortCode: "ar", flag: "/flags/sa.png", name: "العربية" },
  ];

  const currentLanguage = localStorage.getItem("i18nextLng") || "es";
  const selectedLanguage = languageOptions.find(
    lang => lang.code === currentLanguage || lang.shortCode === currentLanguage
  ) || languageOptions[0];
  const signupPath = SIGNUP_LANGUAGE_PATHS[selectedLanguage.shortCode] || "/pt/registre-se";

  const isLangSelected = (lang) =>
    lang.code === currentLanguage || lang.shortCode === currentLanguage;

  const langHeaderLabel = (() => {
    const l = (currentLanguage || "es").split("-")[0];
    return l === "en" ? "Language" : l === "es" ? "Idioma" : l === "tr" ? "Dil" : l === "ar" ? "اللغة" : "Idioma";
  })();

  const backendUrl =
    BACKEND_URL === "https://localhost:8090"
      ? "https://localhost:8090"
      : BACKEND_URL;

  const getLogoPath = () => {
    const isDark = theme.mode === "dark";
    if (isDark && typeof theme.calculatedLoginLogoDark === "function") {
      return theme.calculatedLoginLogoDark();
    }
    if (!isDark && typeof theme.calculatedLoginLogoLight === "function") {
      return theme.calculatedLoginLogoLight();
    }
    return isDark
      ? colorMode.appLogoLoginDark || colorMode.appLogoDark || "/logo-dark.png"
      : colorMode.appLogoLoginLight || colorMode.appLogoLight || "/logo-light.png";
  };

  // Força re-render quando o idioma muda (sem precisar de reload)
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const onLangChanged = () => forceUpdate((n) => n + 1);
    i18n.on("languageChanged", onLangChanged);
    return () => i18n.off("languageChanged", onLangChanged);
  }, []);

  useEffect(() => {
    document.title = i18n.t("login.title");
    document.body.classList.add("login-page");
    return () => {
      document.body.classList.remove("login-page");
    };
  }, []);

  useEffect(() => {
    const fetchUserCreationStatus = async () => {
      try {
        const response = await fetch(`${backendUrl}/settings/userCreation`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        if (!response.ok) throw new Error("Failed to fetch user creation status");
        const data = await response.json();
        setUserCreationEnabled(data.userCreation === "enabled");
      } catch (err) {
        console.error("Erro ao verificar userCreation:", err);
        setUserCreationEnabled(false);
      }
    };
    fetchUserCreationStatus();
  }, [backendUrl]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const lang = localStorage.getItem("i18nextLng") || "es";
    i18n.changeLanguage(lang);
    handleLogin(user);
  };

  const handleLanguageOpen = (event) => setLanguageMenuAnchor(event.currentTarget);
  const handleLanguageClose = () => setLanguageMenuAnchor(null);
  const handleLanguageChange = async (languageCode) => {
    try {
      await i18n.changeLanguage(languageCode);
      localStorage.setItem("i18nextLng", languageCode);
      handleLanguageClose();
      window.location.reload();
    } catch (err) {
      console.error("Erro ao alterar idioma:", err);
    }
  };

  return (
    <div className={classes.root}>
      {/* Controles no topo direito (igual ao header do sistema) */}
      <div className={classes.topRightControls}>
        <div className={classes.langTriggerButton} onClick={handleLanguageOpen}>
          <img
            src={selectedLanguage.flag}
            alt={selectedLanguage.name}
            className={classes.langTriggerFlag}
          />
        </div>
        <ThemeToggle scale={0.75} />
      </div>

      {/* Popover do seletor de idioma */}
      <Popover
        open={Boolean(languageMenuAnchor)}
        anchorEl={languageMenuAnchor}
        onClose={handleLanguageClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{ className: classes.langPopoverPaper }}
        disableScrollLock
      >
        <div className={classes.langHeader}>
          <Typography className={classes.langHeaderTitle}>
            {langHeaderLabel}
          </Typography>
        </div>
        <div className={classes.langOptionsList}>
          {languageOptions.map((lang) => {
            const selected = isLangSelected(lang);
            return (
              <div
                key={lang.code}
                className={`${classes.langOptionItem} ${selected ? classes.langOptionItemSelected : ""}`}
                onClick={() => handleLanguageChange(lang.code)}
              >
                <img
                  src={lang.flag}
                  alt={lang.name}
                  className={classes.langFlagImg}
                />
                <Typography className={classes.langOptionName}>
                  {lang.name}
                </Typography>
                <div className={classes.langCheckWrapper}>
                  {selected && <CheckIcon />}
                </div>
              </div>
            );
          })}
        </div>
      </Popover>

      {/* Card */}
      <div className={classes.cardWrap}>
        <div className={classes.blurLayer}>
          <div className={classes.blurOrb1} />
          <div className={classes.blurOrb2} />
        </div>
        <div className={classes.borderGradient} />

        <div className={classes.card}>
          <div className={classes.cardInner}>
            <img
              src={getLogoPath()}
              alt={i18n.t("login.logoAlt")}
              className={classes.logoImg}
            />

            {/* Header */}
            <div className={classes.header}>
              <div className={classes.secureRow}>
                <span className={classes.secureDot} />
                <span>{i18n.t("login.secureArea")}</span>
              </div>
              <h2 className={classes.title}>{i18n.t("login.title")}</h2>
            </div>

            {/* Form */}
            <form className={classes.form} onSubmit={handleSubmit} noValidate>
              {/* Email */}
              <div>
                <label htmlFor="login-email" className={classes.fieldLabel}>
                  {i18n.t("login.emailLabel")}
                </label>
                <div className={classes.inputWrap}>
                  <span className={classes.inputIconLeft}>
                    <Mail size={16} strokeWidth={1.5} />
                  </span>
                  <input
                    id="login-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    placeholder={i18n.t("login.emailPlaceholder")}
                    value={user.email}
                    onChange={(e) => setUser({ ...user, email: e.target.value })}
                    className={classes.input}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <div className={classes.fieldHeaderRow}>
                  <label htmlFor="login-password" className={classes.fieldLabel}>
                    {i18n.t("login.passwordLabel")}
                  </label>
                  <RouterLink to="/forgot-password" className={classes.forgotLinkInline}>
                    {i18n.t("login.forgotPassword")}
                  </RouterLink>
                </div>
                <div className={classes.inputWrap}>
                  <span className={classes.inputIconLeft}>
                    <Lock size={16} strokeWidth={1.5} />
                  </span>
                  <input
                    id="login-password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    placeholder="••••••••"
                    value={user.password}
                    onChange={(e) => setUser({ ...user, password: e.target.value })}
                    className={`${classes.input} ${classes.inputWithRight}`}
                  />
                  <button
                    type="button"
                    className={classes.inputIconRight}
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={i18n.t("login.togglePassword")}
                  >
                    {showPassword ? (
                      <EyeOff size={16} strokeWidth={1.5} />
                    ) : (
                      <Eye size={16} strokeWidth={1.5} />
                    )}
                  </button>
                </div>
              </div>

              {/* Remember + helper */}
              <div className={classes.optionsRow}>
                <label className={classes.rememberLabel}>
                  <input
                    type="checkbox"
                    name="remember"
                    className={classes.cyberpunkCheckbox}
                    checked={user.remember}
                    onChange={(e) =>
                      setUser({ ...user, remember: e.target.checked })
                    }
                  />
                  <span className={classes.rememberText}>
                    {i18n.t("login.rememberMe")}
                  </span>
                </label>
              </div>

              {/* Submit */}
              <button type="submit" className={classes.submitBtn} disabled={loading}>
                <LogIn size={16} strokeWidth={2} />
                {loading ? "Entrando..." : i18n.t("login.loginButton")}
              </button>

              {userCreationEnabled && (
                <>
                  <div className={classes.divider}>
                    <div className={classes.dividerLine}>
                      <div className={classes.dividerLineInner} />
                    </div>
                    <div className={classes.dividerLabelWrap}>
                      <span className={classes.dividerLabel}>{i18n.t("login.or")}</span>
                    </div>
                  </div>

                  <div className={classes.signupRow}>
                    <p style={{ margin: 0 }}>
                      {i18n.t("login.noAccount")}{" "}
                      <RouterLink to={signupPath} className={classes.signupLink}>
                        {i18n.t("login.signupButton")}
                      </RouterLink>
                    </p>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
